<template>
	<div class="row mb-6">
		<div class="col-md-6 mb-6 mb-md-0">
			<div class="form-floating">
				<input
					id="firstName"
					v-model="firstNameValue"
					name="firstName"
					type="text"
					maxlength="50"
					class="form-control"
					:class="firstNameError ? 'is-invalid' : ''"
					placeholder="First Name"
					data-testid="first-name"
					@blur="firstNameBlur($event, true)"
				/>
				<label for="firstName">First Name&ast;</label>
				<div class="invalid-feedback mt-1" data-testid="first-name-error">{{ firstNameError }}</div>
			</div>
		</div>
		<div class="col-md-6">
			<div class="form-floating">
				<input
					id="lastName"
					v-model="lastNameValue"
					name="lastName"
					type="text"
					maxlength="50"
					class="form-control"
					:class="lastNameError ? 'is-invalid' : ''"
					placeholder="Last Name"
					data-testid="last-name"
					@blur="lastNameBlur($event, true)"
				/>
				<label for="lastName">Last Name&ast;</label>
				<div class="invalid-feedback mt-1" data-testid="last-name-error">{{ lastNameError }}</div>
			</div>
		</div>
	</div>
	<div class="row mb-6">
		<div class="col">
			<div class="form-floating">
				<input
					id="phone"
					v-model="phoneValue"
					v-maska="phoneMaskaOptions"
					name="telephone2"
					type="text"
					class="form-control"
					:class="phoneMeta.touched && phoneError ? 'is-invalid' : ''"
					placeholder="Phone"
					data-testid="phone"
					@blur="phoneBlur($event, true)"
				/>
				<label for="phone">Phone&ast;</label>
				<div class="invalid-feedback mt-1" data-testid="phone-error">
					{{ phoneError }}
				</div>
			</div>
		</div>
	</div>
	<div class="row mb-6">
		<div class="col">
			<div class="form-floating">
				<input
					id="email"
					v-model="emailValue"
					name="emailAddress"
					type="text"
					maxlength="140"
					class="form-control"
					:class="emailMeta.touched && emailError ? 'is-invalid' : ''"
					placeholder="email"
					data-testid="email"
					@blur="emailBlur($event, true)"
				/>
				<label for="email">Email&ast;</label>
				<div class="invalid-feedback mt-1" data-testid="email-error">
					{{ emailError }}
				</div>
			</div>
		</div>
	</div>

	<slot name="additionalFields" />

	<div class="row mb-6">
		<div class="col">
			<div class="form-check ps-0">
				<input
					id="tou"
					v-model="touValue"
					name="tou"
					class="form-check-input ms-0"
					:class="touError ? 'is-invalid' : ''"
					:value="false"
					type="checkbox"
					data-testid="tou"
				/>
				<label class="form-check-label small text-opacity-75 d-block ps-3" for="tou">
					<slot name="touText" />
				</label>
				<div class="invalid-feedback mt-1" data-testid="tou-error">{{ touError }}</div>
			</div>
		</div>
		<slot name="touLinks" />
	</div>
</template>

<script setup lang="ts">
import { vMaska } from 'maska/vue';
import { useField } from 'vee-validate';
import { useMaska } from '@/composables/use-maska';
import { validateEmail, validateName, validatePhone } from '@/util/validators';

const {
	errorMessage: firstNameError,
	handleBlur: firstNameBlur,
	value: firstNameValue
} = useField<string>('firstName', (value: string) =>
	validateName(value, 'Please enter your first name')
);

const {
	errorMessage: lastNameError,
	handleBlur: lastNameBlur,
	value: lastNameValue
} = useField<string>('lastName', (value: string) =>
	validateName(value, 'Please enter your last name')
);

const { phoneMaskaOptions } = useMaska();
const {
	errorMessage: phoneError,
	handleBlur: phoneBlur,
	value: phoneValue,
	meta: phoneMeta
} = useField<string>('telephone2', (value: string) =>
	validatePhone(value, 'Please enter your phone number')
);

const {
	errorMessage: emailError,
	handleBlur: emailBlur,
	value: emailValue,
	meta: emailMeta
} = useField<string>('emailAddress', (value: string) =>
	validateEmail(value, 'Please enter an email address')
);

const { errorMessage: touError, value: touValue } = useField<boolean>('tou', (value: boolean) =>
	value ? true : 'Please accept the terms'
);
</script>
